import { Injectable } from '@angular/core';
import { PageConfig } from '../models/page.model';
import { signal, computed, Signal } from '@angular/core';

@Injectable({
	providedIn: 'root',
})
export class PageStateService {
	private pagesSignal = signal<Map<string, PageConfig>>(
		new Map<string, PageConfig>()
	);

	public readonly allPages = computed(() =>
		Array.from(this.pagesSignal().values())
	);

	constructor() {
		console.log('[PageState] Service initialized');
	}

	addPage(id: string, config: PageConfig) {
		console.log(`[PageState] Adding page ${id} to state`);
		const updatedPages = new Map(this.pagesSignal());
		updatedPages.set(id, config);
		this.pagesSignal.set(updatedPages);
	}

	deletePage(id: string) {
		console.log(`[PageState] Deleting page ${id} from state`);
		const updatedPages = new Map(this.pagesSignal());
		updatedPages.delete(id);
		this.pagesSignal.set(updatedPages);
	}

	updatePage(id: string, updates: Partial<PageConfig>) {
		console.log(`[PageState] Updating page ${id} in state`);
		const pages = this.pagesSignal();
		const current = pages.get(id);

		if (current) {
			const updated = { ...current, ...updates };
			const updatedPages = new Map(pages);
			updatedPages.set(id, updated);
			this.pagesSignal.set(updatedPages);
		}
	}

	getPage(id: string): PageConfig | undefined {
		return this.pagesSignal().get(id);
	}

	getAllPages(): Map<string, PageConfig> {
		return this.pagesSignal();
	}

	getPageSignal(id: string): Signal<PageConfig | undefined> {
		return computed(() => this.pagesSignal().get(id));
	}

	clearAllPages(): void {
		console.log(`[PageState] Clearing all pages from state`);
		this.pagesSignal.set(new Map<string, PageConfig>());
	}

	/**
	 * Reorders pages by rebuilding the Map in the specified order.
	 * Map iteration order = insertion order, so this controls display order.
	 * Updates each page's `order` field to match its new index (1-based).
	 */
	reorderPages(orderedIds: string[]): void {
		const pages = this.pagesSignal();
		const newMap = new Map<string, PageConfig>();
		orderedIds.forEach((id, index) => {
			const config = pages.get(id);
			if (config) {
				newMap.set(id, { ...config, order: index + 1 });
			}
		});
		// Include any pages not in orderedIds (e.g. from race conditions) at the end
		for (const [id, config] of pages) {
			if (!newMap.has(id)) {
				newMap.set(id, { ...config, order: newMap.size + 1 });
			}
		}
		this.pagesSignal.set(newMap);
	}
}
